/**
 * Decoder for the `zlib_uint8` codec — the inverse of
 * `organism.observatory.artifacts.write_activity_artifact`/`quantize_activity`.
 *
 * Python's `zlib.compress` writes the RFC 1950 "zlib" container (a 2-byte
 * header + DEFLATE stream + Adler-32 trailer), which is exactly what the
 * browser's `DecompressionStream("deflate")` format name expects (`"deflate"`
 * = zlib container, `"deflate-raw"` = bare RFC 1951 DEFLATE — the format
 * this codebase's `npz.ts` reader needs instead, for zip members).
 */

const ACTIVITY_RANGE = 1.0;
const UINT8_MAX = 255;

export class ZlibActivityParseError extends Error {}

async function inflateZlib(bytes: ArrayBuffer): Promise<Uint8Array> {
  const stream = new DecompressionStream("deflate");
  const writer = stream.writable.getWriter();
  void writer.write(new Uint8Array(bytes));
  void writer.close();
  const chunks: Uint8Array[] = [];
  const reader = stream.readable.getReader();
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    if (value) chunks.push(value);
  }
  const total = chunks.reduce((sum, chunk) => sum + chunk.length, 0);
  const out = new Uint8Array(total);
  let position = 0;
  for (const chunk of chunks) {
    out.set(chunk, position);
    position += chunk.length;
  }
  return out;
}

export interface ActivityGrid {
  /** `shape[0]` = K sampled steps, `shape[1]` = N neurons. */
  readonly shape: readonly [number, number];
  /** Row-major `uint8[K*N]`, one quantized activity byte per (step, neuron). */
  readonly raw: Uint8Array;
}

/** Inflates a `zlib_uint8` artifact's bytes into its `uint8[K, N]` grid. */
export async function decodeActivityGrid(
  compressed: ArrayBuffer,
  shape: readonly number[],
): Promise<ActivityGrid> {
  if (shape.length !== 2) {
    throw new ZlibActivityParseError(
      `activity artifact shape must be 2-D, got ${JSON.stringify(shape)}`,
    );
  }
  const [steps, neurons] = shape as [number, number];
  const raw = await inflateZlib(compressed);
  if (raw.length !== steps * neurons) {
    throw new ZlibActivityParseError(
      `inflated activity grid is ${raw.length} bytes, expected ${steps * neurons} (shape ${JSON.stringify(shape)})`,
    );
  }
  return { shape: [steps, neurons], raw };
}

/** Dequantizes one uint8 activity byte back to the core's tanh range `[-1, 1]`
 * (approximate inverse of `quantize_activity`). */
export function dequantizeActivityByte(byte: number): number {
  const scaled = byte / UINT8_MAX;
  return scaled * (2 * ACTIVITY_RANGE) - ACTIVITY_RANGE;
}

/** Dequantizes a full grid into a `Float32Array` the renderer can upload
 * directly as a `BufferAttribute` (see `src/brain/BrainScene.ts`). */
export function dequantizeActivityGrid(grid: ActivityGrid): Float32Array {
  const output = new Float32Array(grid.raw.length);
  for (let index = 0; index < grid.raw.length; index += 1) {
    output[index] = dequantizeActivityByte(grid.raw[index] ?? 0);
  }
  return output;
}
